/**
 * Time Parsing Utilities for 24-hour format support
 * 
 * Supports:
 * - 24-hour format: 18:00, 1800
 * - 12-hour format: 6pm, 6:00pm, 6:00 PM (nice-to-have)
 * - Auto-formatting: 1800 → 18:00
 */

/**
 * Parse various time input formats to HH:MM 24-hour format
 * @param {string} input - Time input in various formats
 * @returns {string|null} HH:MM format or null if invalid
 */
export function parseTimeInput(input) {
  if (!input || typeof input !== 'string') return null;
  
  // Clean input
  let clean = input.trim().toUpperCase();
  
  // Already in HH:MM format
  const hhmmMatch = clean.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmmMatch) {
    const h = parseInt(hhmmMatch[1], 10);
    const m = parseInt(hhmmMatch[2], 10);
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }
    return null;
  }
  
  // Shorthand format: 1800, 0600, 800, etc.
  const shorthandMatch = clean.match(/^(\d{1,4})$/);
  if (shorthandMatch) {
    const num = shorthandMatch[1];
    let h, m;
    
    if (num.length <= 2) {
      // Just hour: "6" → 06:00, "18" → 18:00
      h = parseInt(num, 10);
      m = 0;
    } else if (num.length === 3) {
      // 3 digits: "630" → 06:30, "830" → 08:30
      h = parseInt(num[0], 10);
      m = parseInt(num.slice(1), 10);
    } else {
      // 4 digits: "1800" → 18:00, "0630" → 06:30
      h = parseInt(num.slice(0, 2), 10);
      m = parseInt(num.slice(2), 10);
    }
    
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }
    return null;
  }
  
  // AM/PM format: 6pm, 6:00pm, 6:00 PM, 18:00pm (ignore pm if 24h)
  const ampmMatch = clean.match(/^(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?$/);
  if (ampmMatch) {
    let h = parseInt(ampmMatch[1], 10);
    const m = ampmMatch[2] ? parseInt(ampmMatch[2], 10) : 0;
    const period = ampmMatch[3];
    
    // Handle AM/PM
    if (period === 'PM' && h < 12) {
      h += 12;
    } else if (period === 'AM' && h === 12) {
      h = 0;
    }
    
    if (h >= 0 && h <= 23 && m >= 0 && m <= 59) {
      return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    }
    return null;
  }
  
  return null;
}

/**
 * Format time to display format based on preference
 * @param {string} time - Time in HH:MM format
 * @param {string} format - '24h' or '12h'
 * @returns {string} Formatted time
 */
export function formatTimeDisplay(time, format = '24h') {
  if (!time) return '';
  
  const parsed = parseTimeInput(time);
  if (!parsed) return time;
  
  const [hours, minutes] = parsed.split(':').map(Number);
  
  if (format === '12h') {
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours % 12 || 12;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${period}`;
  }
  
  return parsed;
}

/**
 * Validate time input
 * @param {string} input - Time input
 * @returns {boolean} True if valid
 */
export function isValidTimeInput(input) {
  return parseTimeInput(input) !== null;
}

/**
 * Convert 24-hour time to minutes since midnight
 * @param {string} time - Time in HH:MM format
 * @returns {number} Minutes since midnight
 */
export function timeToMinutes(time) {
  const parsed = parseTimeInput(time);
  if (!parsed) return 0;
  
  const [hours, minutes] = parsed.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Convert minutes since midnight to HH:MM
 * @param {number} minutes - Minutes since midnight
 * @returns {string} Time in HH:MM format
 */
export function minutesToTime(minutes) {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
}

/**
 * Calculate duration between two times in hours
 * @param {string} startTime - Start time HH:MM
 * @param {string} endTime - End time HH:MM
 * @returns {number} Duration in hours (handles overnight)
 */
export function calculateDuration(startTime, endTime) {
  const startMinutes = timeToMinutes(startTime);
  const endMinutes = timeToMinutes(endTime);
  
  let diff = endMinutes - startMinutes;
  if (diff < 0) {
    diff += 24 * 60; // Handle overnight
  }
  
  return diff / 60;
}

export default {
  parseTimeInput,
  formatTimeDisplay,
  isValidTimeInput,
  timeToMinutes,
  minutesToTime,
  calculateDuration
};
